import {
  createConfig,
  http,
  injected,
  connect,
  disconnect,
  getConnection,
  getConnectors,
  getPublicClient,
  readContract,
  readContracts,
  signTypedData,
  switchChain,
  waitForTransactionReceipt,
  watchConnection,
  writeContract
} from '@wagmi/core'
import { mainnet } from '@wagmi/core/chains'
import { estimateContractGas } from 'viem/actions'
import { BaseError, UserRejectedRequestError, parseEventLogs } from 'viem'
import { Client as Web3UrlClient } from 'web3protocol'
import RADIOACTIVEPUNKS from '@/assets/RADIOACTIVEPUNKS.json'

export const RPUNKS_ADDRESS = '0x073Ca28E04719C05a5a48C1d992091b4075A0F84'
export const RPETS_ADDRESS = '0x3a7E7b26f779DE6D12A675caa34419625Aa67159'

// the contracts verify this login signature when minting, so it must stay byte-for-byte the same
const LOGIN_TYPED_DATA = {
  domain: {
    name: 'RADIOACTIVEPUNKS',
    version: '1',
    chainId: mainnet.id,
    verifyingContract: '0xFc9961d08Ef8e04DB145b9fb3d48cf8DbA96116D'
  },
  types: {
    Unit: [
      { name: 'logIn', type: 'string' }
    ]
  },
  primaryType: 'Unit',
  message: {
    logIn: 'Log In To Radioactive Punks!'
  }
}

// set VUE_APP_MAINNET_RPC_URL to use a dedicated RPC instead of viem's public default
const MAINNET_RPC_URL = process.env.VUE_APP_MAINNET_RPC_URL || mainnet.rpcUrls.default.http[0]

export const config = createConfig({
  chains: [mainnet],
  connectors: [injected()],
  transports: {
    [mainnet.id]: http(MAINNET_RPC_URL)
  }
})

// resolves ERC-6860 / ERC-4804 web3:// URLs (like the punks' tokenURIs) by calling the contracts they point at
const web3UrlClient = new Web3UrlClient([{
  id: mainnet.id,
  name: mainnet.name,
  shortName: 'eth',
  rpcUrls: [MAINNET_RPC_URL],
  contracts: mainnet.contracts
}])

export function read (request) {
  return readContract(config, { chainId: mainnet.id, ...request })
}

export function readMany (contracts) {
  return readContracts(config, { allowFailure: false, contracts })
}

// a punk's metadata, fully from chain: the original RPUNKS contract's tokenURI is a web3:// URL
// pointing at its on-chain metadata renderer, which we then call for the JSON
export async function fetchPunkMetadata (tokenId) {
  const uri = await read({
    address: RPUNKS_ADDRESS,
    abi: RADIOACTIVEPUNKS.abi,
    functionName: 'tokenURI',
    args: [BigInt(tokenId)]
  })
  const response = await web3UrlClient.fetchUrl(uri)
  if (response.httpCode !== 200) {
    throw new Error(`${uri} returned HTTP ${response.httpCode}`)
  }
  return new Response(response.output).json()
}

export function onConnectionChange (callback) {
  return watchConnection(config, { onChange: callback })
}

// EIP-6963 discovered wallets plus the generic window.ethereum fallback,
// which is only shown when nothing announced itself (e.g. mobile in-app browsers)
export function availableConnectors () {
  const connectors = getConnectors(config)
  const announced = connectors.filter(c => c.id !== 'injected')
  return announced.length ? announced : connectors
}

export function isWalletConnected () {
  return getConnection(config).isConnected
}

export function hasWallet () {
  return getConnectors(config).some(c => c.id !== 'injected') || !!window.ethereum
}

// connects (if needed) with the given connector and makes sure we're on mainnet
export async function connectWallet (connector) {
  let connection = getConnection(config)
  if (!connection.isConnected) {
    await connect(config, { connector, chainId: mainnet.id })
    connection = getConnection(config)
  }
  if (connection.chainId !== mainnet.id) {
    await switchChain(config, { chainId: mainnet.id })
  }
  return connection.address
}

export function disconnectWallet () {
  return disconnect(config)
}

// prompts the EIP-712 login signature and splits it into the v/r/s the contracts expect
export async function signLogin (address) {
  const signature = await signTypedData(config, { account: address, ...LOGIN_TYPED_DATA })
  return parseSignature(signature.substring(2))
}

function parseSignature (signature) {
  return {
    r: '0x' + signature.substring(0, 64),
    s: '0x' + signature.substring(64, 128),
    v: parseInt(signature.substring(128, 130), 16)
  }
}

export async function estimateGasWithBuffer ({ account, ...request }) {
  const gas = await estimateContractGas(getPublicClient(config), { account, ...request })
  return (gas * 12n) / 10n
}

export function sendContractTx (request) {
  return writeContract(config, { chainId: mainnet.id, ...request })
}

// waits for one confirmation and returns the token ids transferred by `address` in that tx.
// throws errors shaped like the old ethers ones (CALL_EXCEPTION / TRANSACTION_REPLACED)
// so callers can keep their existing error handling
export async function waitForMintedTokenIds (hash, address, abi) {
  let replacement
  const receipt = await waitForTransactionReceipt(config, {
    hash,
    chainId: mainnet.id,
    onReplaced: (r) => { replacement = r }
  })

  if (replacement && replacement.reason !== 'repriced') {
    throw Object.assign(new Error(`Transaction ${replacement.reason}`), {
      code: 'TRANSACTION_REPLACED',
      reason: replacement.reason,
      replacement: { transactionHash: replacement.transaction.hash }
    })
  }
  if (receipt.status === 'reverted') {
    throw Object.assign(new Error('Transaction reverted'), {
      code: 'CALL_EXCEPTION',
      transactionHash: receipt.transactionHash
    })
  }

  return parseEventLogs({ abi, logs: receipt.logs, eventName: 'Transfer' })
    .filter(log => log.address.toLowerCase() === address.toLowerCase())
    .map(log => Number(log.args.tokenId))
}

export function isUserRejection (e) {
  if (e instanceof BaseError) {
    return !!e.walk(err => err instanceof UserRejectedRequestError || err.code === 4001)
  }
  return !!(e && e.code === 4001)
}
